import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DEAL_ORIGINS, ORIGIN_LABEL, type OriginRates, type PostTermRule } from "@talentkontor/domain";
import { api } from "../../../data";
import { useAction } from "../../../components/ui";
import { NachlaufRegelFelder } from "../../../components/NachlaufRegel";
import { herkunftAusFormular, herkunftFormular, regelAusFormular, regelFormular, type HerkunftFormular, type RegelFormular } from "../../../model/nachlauf";
import { bpToPercentText, welcherSatzGilt } from "./model";
import { t, currentLang } from "../../../lib/sprache";
import { Klappabschnitt } from "./Klappabschnitt";

/**
 * Einstellungen · Konditionen (0074): Provisionssatz je Herkunft der Kooperation und Standard der Nachlaufregel beim
 * Archivieren eines Creators. Beides je Agentur in org_settings (origin_commission_bp, post_term_default); leer = kein eigener
 * Satz bzw. keine Nachprovision. Nur Admins (Konditionen).
 */
interface Werte { origin_commission_bp: OriginRates | null; post_term_default: PostTermRule | null; default_commission_bp: number | null }

export function HerkunftNachlauf({ orgId }: { orgId: string }) {
  const q = useQuery({
    queryKey: ["org_settings", orgId, "herkunft_nachlauf"],
    queryFn: async () => (await api.select<Werte>("org_settings", { columns: "origin_commission_bp, post_term_default, default_commission_bp", filters: [["org_id", "eq", orgId]] }))[0] ?? null,
  });
  if (q.isPending || q.error || !q.data) return null;
  return <Formular key={JSON.stringify(q.data)} orgId={orgId} werte={q.data} />;
}

function Formular({ orgId, werte }: { orgId: string; werte: Werte }) {
  const { busy, error, run } = useAction();
  const [herkunft, setHerkunft] = useState<HerkunftFormular>(() => herkunftFormular(werte.origin_commission_bp));
  const [regel, setRegel] = useState<RegelFormular>(() => regelFormular(werte.post_term_default));
  const [fehler, setFehler] = useState<string | null>(null);
  const std = werte.default_commission_bp;
  const geaendert = JSON.stringify([herkunft, regel]) !== JSON.stringify([herkunftFormular(werte.origin_commission_bp), regelFormular(werte.post_term_default)]);
  const eigeneSaetze = DEAL_ORIGINS.filter((o) => werte.origin_commission_bp?.[o] != null).length;
  const zusammenfassung = [
    t(`Eigener Satz für ${eigeneSaetze} von ${DEAL_ORIGINS.length} Herkünften`, `Own rate for ${eigeneSaetze} of ${DEAL_ORIGINS.length} origins`),
    werte.post_term_default ? t("Nachprovision: Standardregel", "Post-term commission: default rule") : t("keine Nachprovision", "no post-term commission"),
  ].join(" · ");

  function speichern() {
    const h = herkunftAusFormular(herkunft);
    const r = regelAusFormular(regel);
    const f = h.fehler ?? r.fehler;
    setFehler(f);
    if (f) return;
    void run("herkunft", async () => {
      const rows = await api.update("org_settings", [["org_id", "eq", orgId]], { origin_commission_bp: h.rates, post_term_default: r.rule });
      if (!rows.length) throw new Error(t("Nicht gespeichert. Nur Admins ändern die Konditionen.", "Not saved. Only admins change the terms."));
    }, t("Herkunft und Nachprovision gespeichert. Neue Sätze gelten für neue Kooperationen.", "Origin and post-term commission saved. New rates apply to new deals."));
  }

  return (
    <Klappabschnitt id="herkunft-nachlauf" titel={t("Herkunft und Nachprovision", "Origin and post-term commission")} zusammenfassung={zusammenfassung} geaendert={geaendert}>
      <h3>{t("Provision nach Herkunft der Kooperation", "Commission by deal origin")}</h3>
      <p className="source-note">{welcherSatzGilt()}</p>
      <p>
        {t("Beim Anlegen einer Kooperation lässt sich angeben, woher sie kommt. Ein Satz hier geht dann dem Standard des Creators und der Agentur vor (z. B. weniger Provision bei Eigenakquise) und wird wie jeder Satz festgeschrieben. Leer = Standard", "When creating a deal you can state where it comes from. A rate here then takes precedence over the creator's and the agency's default (e.g. less commission for own acquisition) and is fixed like any rate. Empty = default")}{std != null ? ` (${bpToPercentText(std)}\u00a0%)` : ""}.
      </p>
      <div className="grid">
        {DEAL_ORIGINS.map((o) => (
          <label key={o}>
            {ORIGIN_LABEL[o][currentLang()]} {t("in Prozent", "in percent")}<input id={`herkunft-${o}`} inputMode="decimal" value={herkunft[o]} placeholder={t("Standard", "Default")} onChange={(e) => setHerkunft({ ...herkunft, [o]: e.target.value })} />
          </label>
        ))}
      </div>
      <h3>{t("Nachprovision nach Austritt eines Creators", "Post-term commission after a creator leaves")}</h3>
      <p>
        {t("Beim Archivieren fragt der Dialog nach einer Nachlaufregel und schlägt diesen Standard vor. Ohne Standard gibt es keine Nachprovision, außer die Regel wird beim Archivieren eigens eingetragen (etwa weil der Managementvertrag sie nennt).", "When archiving, the dialog asks for a post-term rule and suggests this default. Without a default there is no post-term commission unless the rule is entered specifically when archiving (for example because the management contract names it).")}</p>
      <label className="row">
        <input type="checkbox" checked={regel.aktiv} onChange={(e) => setRegel({ ...regel, aktiv: e.target.checked })} />
        {t("Standard-Nachlaufregel verwenden", "Use default post-term rule")}</label>
      {regel.aktiv && <NachlaufRegelFelder f={regel} set={(p) => setRegel({ ...regel, ...p })} idPrefix="nachlauf-standard" />}
      {(fehler ?? error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}
      <div className="row">
        <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "herkunft"} onClick={speichern}>
          {busy === "herkunft" ? t("Wird gespeichert …", "Saving …") : t("Speichern", "Save")}
        </button>
      </div>
    </Klappabschnitt>
  );
}
